import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "./utils/ResponsiveImage";
import {
  getShowcaseItems,
  getShowcaseGroup,
  getImageSrc,
  getLowestRate,
  formatPrice,
  ShowcaseItem,
} from "./utils/showcase";
import { useSliderState } from "../SliderState";
import localization from "../../../../../assets/localization";
import Analytics from "../../../../../utils/Analytics";
import Utility from "../../../logic/Utility";
import { useBookingEnabled } from "../booking/BookingState";
import { isBookableItem, startBookingFromShowcase } from "../booking/fromShowcase";

interface ShowcaseCardProps {
  template: any;
  language: string;
}

const LONG_DESCRIPTION = 140;

export function ShowcaseCTAs({
  item,
  language,
  template,
}: {
  item: ShowcaseItem;
  language: string;
  template?: any;
}) {
  const sliderState = useSliderState();
  const t = localization.get(language);
  const bookingEnabled = useBookingEnabled();

  // Booking has to start from the docked card too, not just the immersive
  // view: on prod only 36 of 125 quote views ever went fullscreen, so wiring
  // the immersive surface alone would leave most guests on the deep link.
  if (bookingEnabled && isBookableItem(item)) {
    return (
      <button
        className="showcase-cta"
        onClick={() => startBookingFromShowcase(item, template)}
      >
        {t.book_now}
      </button>
    );
  }

  if (item.bookUrl) {
    return (
      <a
        className="showcase-cta"
        href={item.bookUrl}
        target="_blank"
        rel="noreferrer"
        onClick={() =>
          Analytics.track("link_click", {
            url: item.bookUrl,
            cta: "book_now",
            source: "showcase",
            item_title: item.title ?? null,
          })
        }
      >
        {t.book_now}
      </a>
    );
  }

  if (item.ctas) {
    return (
      <>
        {item.ctas
          .filter((cta: any) => cta.type === "url")
          .map((cta: any, index: number) => (
            <a
              key={index}
              className={`showcase-cta ${index > 0 ? "secondary" : ""}`}
              href={Utility.getWithParams(
                cta.link,
                sliderState.assistantId,
                sliderState.threadId,
                cta.key === "quick_quote"
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                Analytics.track("link_click", {
                  url: cta.link,
                  cta: cta.key,
                  source: "showcase",
                  item_title: item.title ?? null,
                })
              }
            >
              {t[cta.key] ?? t.open_content}
            </a>
          ))}
      </>
    );
  }

  return null;
}

export default function ShowcaseCard({ template, language }: ShowcaseCardProps) {
  const sliderState = useSliderState();
  const t = localization.get(language);
  const reduceMotion = useReducedMotion();

  const items = useMemo(() => getShowcaseItems(template), [template]);
  const group = useMemo(() => getShowcaseGroup(template), [template]);
  const itemIndex = Math.max(
    0,
    Math.min(sliderState.subTemplateItemIndex ?? 0, items.length - 1)
  );
  const item = items[itemIndex];

  const [imageIndex, setImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const lastCenteredChip = useRef(-1);

  useEffect(() => {
    setImageIndex(0);
  }, [itemIndex, template]);

  // Ref callback on the ACTIVE chip: the card remounts through
  // AnimatePresence on item change, so an effect would scroll the outgoing
  // DOM · this runs when the new card's chip actually mounts.
  const centerActiveChip = (chip: HTMLButtonElement | null) => {
    if (!chip || lastCenteredChip.current === itemIndex) return;
    const rail = chip.parentElement;
    if (!rail) return;
    lastCenteredChip.current = itemIndex;
    rail.scrollTo({
      left: Math.max(0, chip.offsetLeft - (rail.clientWidth - chip.offsetWidth) / 2),
      behavior: "smooth",
    });
  };

  // Keyword clicks in chat messages jump straight to the matching item.
  useEffect(() => {
    if (!sliderState.selectedKeyword) return;
    const index = items.findIndex(
      (i) => i.title?.toLowerCase() === sliderState.selectedKeyword
    );
    if (index !== -1 && index !== itemIndex) {
      sliderState.setSubTemplateItemIndex(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderState.selectedKeyword]);

  if (!item) return null;
  const images = item.images ?? [];

  // Arrows walk photos, then flow into the next/previous item of THIS
  // template (circular). They never jump to another template · that is what
  // the chat anchors and the Esplora hub are for.
  const nextImage = () => {
    if (imageIndex < images.length - 1) {
      setImageIndex((i) => i + 1);
      return;
    }
    if (items.length > 1) {
      sliderState.setSubTemplateItemIndex((itemIndex + 1) % items.length);
      return;
    }
    setImageIndex(0);
  };

  const prevImage = () => {
    if (imageIndex > 0) {
      setImageIndex((i) => i - 1);
      return;
    }
    if (items.length > 1) {
      sliderState.setSubTemplateItemIndex(
        (itemIndex - 1 + items.length) % items.length
      );
      return;
    }
    setImageIndex(Math.max(0, images.length - 1));
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) nextImage();
    else prevImage();
  };

  const isLongDescription =
    (item.description ?? "").length > LONG_DESCRIPTION;
  const hasItemDescription = (item.description ?? "").trim().length > 0;

  // The photo the guest is looking at names the card. Its KB-authored title
  // and description are the caption, and they take the headline: what is on
  // screen is a photo, not the fact as a whole. The fact title is not lost ·
  // it moves up to the context pill on the hero, which otherwise only carries
  // a generic category word ("Da scoprire").
  //
  // Before, the caption was a glass plate floating above the card, over the
  // photo: a second competing title block, dark text on an unpredictable
  // background, and it pushed the card's own title into being a duplicate.
  const activeImage = images[imageIndex];
  const photoTitle = (activeImage?.title ?? "").trim();
  const photoDescription = (activeImage?.description ?? "").trim();
  const headline = photoTitle || item.title;
  const categoryLabel =
    template.type === "quote" ? t.explore_rooms : t.explore_discover;
  const hasNamedContext = !!photoTitle && !!item.title;

  return (
    <div
      className="showcase-root"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Hero images, stacked (so they preload); the active one blurs through */}
      {images.map((image, index) => (
        <ResponsiveImage
          key={`${itemIndex}-${index}`}
          src={getImageSrc(image)}
          alt={image.description ?? item.title}
          className={`showcase-hero-img ${
            index === imageIndex ? "active" : ""
          }`}
        />
      ))}

      <div className="showcase-scrim-top" />

      {/* Where am I: the fact (or its category) + position within this template */}
      <div className={`showcase-context ${hasNamedContext ? "named" : ""}`}>
        <span className="showcase-context-label">
          {hasNamedContext ? item.title : categoryLabel}
        </span>
        {items.length > 1 && (
          <span className="showcase-context-count">
            {itemIndex + 1}/{items.length}
          </span>
        )}
      </div>

      {/* Stories-style image progress */}
      {images.length > 1 && (
        <div className="showcase-progress">
          {images.map((_, index) => (
            <div
              key={index}
              className={`showcase-progress-seg ${
                index === imageIndex ? "active" : ""
              }`}
              onClick={() => setImageIndex(index)}
            />
          ))}
        </div>
      )}

      {/* Image navigation */}
      <button
        className="showcase-nav-btn"
        style={{ left: "10px" }}
        onClick={prevImage}
        aria-label="Previous"
      >
        <i className="fa-solid fa-chevron-left"></i>
      </button>
      <button
        className="showcase-nav-btn"
        style={{ right: "10px" }}
        onClick={nextImage}
        aria-label="Next"
      >
        <i className="fa-solid fa-chevron-right"></i>
      </button>

      {/* Floating content card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={itemIndex}
          className="showcase-card"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div className="showcase-title-row">
            {/* Keyed on the photo: the heading belongs to the image on screen,
                so it re-enters with it instead of silently swapping text. */}
            <motion.div
              key={`photo-${imageIndex}`}
              className="showcase-heading"
              initial={reduceMotion ? false : { opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.26, ease: "easeOut" }}
            >
              <div className="showcase-title">{headline}</div>
              {photoDescription && (
                <div className="showcase-caption">{photoDescription}</div>
              )}
            </motion.div>
            {group && group.total !== null ? (
              <div className="showcase-price">
                <span>{t.quote_group.total}</span>
                {formatPrice(group.total)}
              </div>
            ) : getLowestRate(item) !== null ? (
              <div className="showcase-price">
                <span>{t.from_price}</span>
                {formatPrice(getLowestRate(item)!)}
              </div>
            ) : (
              template.type === "quote" && (
                <div className="showcase-chip">{t.available}</div>
              )
            )}
          </div>

          {group && (
            <div className="showcase-group-rooms">
              {t.quote_group.rooms.replace("{n}", String(group.members.length))}
            </div>
          )}

          {items.length > 1 && (
            <div className="showcase-item-chips">
              {items.map((chipItem, index) => (
                <button
                  key={index}
                  ref={index === itemIndex ? centerActiveChip : undefined}
                  className={`showcase-item-chip ${
                    index === itemIndex ? "active" : ""
                  }`}
                  onClick={() => sliderState.setSubTemplateItemIndex(index)}
                >
                  {chipItem.title || `${index + 1}`}
                </button>
              ))}
            </div>
          )}

          {hasItemDescription && (
            <div
              className={`showcase-desc ${photoDescription ? "with-caption" : ""}`}
            >
              {item.isMarkdown ? (
                <ReactMarkdown>{item.description ?? ""}</ReactMarkdown>
              ) : (
                item.description
              )}
            </div>
          )}

          <div className="showcase-actions">
            <ShowcaseCTAs item={item} language={language} template={template} />
            {isLongDescription && (
              <button
                className="showcase-readmore"
                onClick={() => sliderState.setExpanded(true)}
              >
                {t.read_more}
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
